/**
 * Color de marca y contraste.
 *
 * El cliente elige su color corporativo sin pensar en accesibilidad, así que el color del texto que
 * va encima **se calcula**, no se fija: con uno claro, texto blanco sería ilegible (A11Y-04).
 */

/** Color por defecto cuando la publicación no define ninguno. */
export const DEFAULT_PRIMARY = '#0f766e';

/** Texto oscuro para fondos claros. No es negro puro: sobre un color saturado cansa menos. */
const DARK_FOREGROUND = '#111827';

const LIGHT_FOREGROUND = '#ffffff';

/**
 * Valida un color en `#RRGGBB` y lo normaliza a minúsculas. Devuelve `null` en lugar de un color de
 * reserva: quien llama decide qué hacer con la ausencia.
 */
export function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;

  const candidate = value.trim().toLowerCase();

  return /^#[0-9a-f]{6}$/.test(candidate) ? candidate : null;
}

/** Luminancia relativa según WCAG 2.1. */
function relativeLuminance(hex: string): number {
  const channels = [1, 3, 5].map((offset) => {
    const value = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;

    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * (channels[0] ?? 0) + 0.7152 * (channels[1] ?? 0) + 0.0722 * (channels[2] ?? 0);
}

function contrastRatio(a: number, b: number): number {
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);

  return (lighter + 0.05) / (darker + 0.05);
}

/** Color de texto legible sobre el color dado: el de los dos que más contraste ofrece. */
export function readableForeground(hex: string): string {
  const background = relativeLuminance(hex);

  const onLight = contrastRatio(background, relativeLuminance(DARK_FOREGROUND));
  const onDark = contrastRatio(background, relativeLuminance(LIGHT_FOREGROUND));

  return onDark >= onLight ? LIGHT_FOREGROUND : DARK_FOREGROUND;
}
